import dayjs from 'dayjs';
import { buildApiUrl } from '../../../utils/createUrl';
import { camelizeKeys } from 'humps';

const AVAILABILITY_KEY_FORMAT = 'YYYY-MM-DD';

export const clampDateToMax = (date, maxDate) => {
  if (!date) return date;

  const parsedDate = dayjs(date);
  const parsedMaxDate = dayjs(maxDate);
  if (!parsedDate.isValid() || !parsedMaxDate.isValid()) return date;

  return parsedDate.isAfter(parsedMaxDate, 'day')
    ? parsedMaxDate.startOf('day').toDate()
    : date;
};

/**
 * Calculate the availability fetch window anchored at the given date.
 *
 * - `from` = the anchor day itself.
 * - `to`   = the earlier of the anchor month's last day and `maxDate`.
 *   Navigating to a later month fetches that month on demand, keeping each
 *   request ~1 month and never requesting days the picker cannot select.
 *
 * Invalid or missing input falls back to "now" (matches the historical
 * behavior of the native-Date implementation).
 *
 * @param {Date|string|null} [visibleDate]
 * @param {Date|string|null} [maxDate]
 * @returns {{ from: string, to: string }} YYYY-MM-DD strings, local time.
 */
export const calculateAvailabilityWindow = (visibleDate = null, maxDate = null) => {
  const anchor = dayjs(visibleDate);
  const start = anchor.isValid() ? anchor : dayjs();
  const boundedStart = dayjs(clampDateToMax(start.toDate(), maxDate));
  const parsedMaxDate = dayjs(maxDate);
  const monthEnd = boundedStart.endOf('month');
  const end = parsedMaxDate.isValid() && parsedMaxDate.isBefore(monthEnd, 'day')
    ? parsedMaxDate
    : monthEnd;

  return {
    from: boundedStart.format(AVAILABILITY_KEY_FORMAT),
    to: end.format(AVAILABILITY_KEY_FORMAT),
  };
};

export const buildAvailabilityApiUrl = ({
  origin,
  destination,
  from,
  to,
  sourceUrl,
  forceHttps = false,
  round = false,
}) => {
  if (!origin || !destination || !from || !to || !sourceUrl) return null;

  const cleanedSourceUrl = sourceUrl.replace(/\/places$/, '');
  const url = buildApiUrl({ sourceUrl: cleanedSourceUrl, path: '/search/calendar', forceHttps });

  url.setQueryParam('origin', origin);
  url.setQueryParam('destination', destination);
  url.setQueryParam('from', from);
  url.setQueryParam('to', to);
  if (round) {
    url.setQueryParam('round', 'true');
  }

  return url.href;
};

export const indexByDate = (rawDays) => {
  if (!Array.isArray(rawDays)) return {};

  return rawDays.reduce((acc, raw) => {
    if (!raw || !raw.date) return acc;

    const camelized = camelizeKeys(raw);
    // The calendar shows the day-level `display_price` (a flat number sibling of
    // `min_price`/`price_level`), not the nested `min_price.pricing`.
    const minPrice = Number.isFinite(camelized.displayPrice) ? camelized.displayPrice : null;

    acc[camelized.date] = { ...camelized, minPrice };
    return acc;
  }, {});
};
